import { type ImportJobListItem } from '@utazzo/shared';
import { UzBadge, UzButton, UzCard, UzPagination, UzSpinner } from '@utazzo/ui';
import { ChevronRight, Plus, type LucideIcon } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageShell } from '@/components/page-shell';
import { useImportJobs } from '@/features/lead-imports/use-imports';
import { useListReturn } from '@/hooks/use-list-return';

/**
 * Generic audit page scoped to a single import moduleKey. Used by every
 * per-module importer that has its own audit log page (transfers / visas /
 * meal services / activities / hotels).
 */
export interface SingleModuleAuditPageConfig {
  moduleKey: string;
  /** "transfers" / "visas" / "meal services" */
  titlePlural: string;
  /** Route prefix matching the SingleSheetImportPage's prefix. */
  routePrefix: string;
  /** "Back to list" header button. */
  listPath: string;
  ListIcon: LucideIcon;
}

export const SingleModuleAuditPage = ({
  config,
}: {
  config: SingleModuleAuditPageConfig;
}): React.JSX.Element => {
  const navigate = useNavigate();
  const backToList = useListReturn(config.listPath);
  const [page, setPage] = useState(1);
  const list = useImportJobs({
    page,
    pageSize: 25,
    sortDir: 'desc',
    sortBy: 'startedAt',
    moduleKey: config.moduleKey,
  });
  const ListIcon = config.ListIcon;

  return (
    <PageShell
      title={`${cap(config.titlePlural)} imports`}
      description={`Audit log of ${config.titlePlural} bulk imports.`}
      actions={
        <div className="flex gap-2">
          <UzButton variant="ghost" size="sm" onClick={backToList}>
            <ListIcon className="mr-1.5 h-4 w-4" />
            {cap(config.titlePlural)} list
          </UzButton>
          <UzButton size="sm" onClick={() => navigate(`/${config.routePrefix}/imports/new`)}>
            <Plus className="mr-1.5 h-4 w-4" />
            New import
          </UzButton>
        </div>
      }
    >
      <UzCard>
        {list.isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-zinc-500">
            <UzSpinner size="sm" /> Loading…
          </div>
        ) : (list.data?.data ?? []).length === 0 ? (
          <div className="py-8 text-center text-sm text-zinc-500">
            No {config.titlePlural} import jobs yet. Click <strong>New import</strong> above to
            start one.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 text-left text-xs font-medium tracking-wide text-zinc-500 uppercase">
                    <th className="px-2 py-2">Started</th>
                    <th className="px-2 py-2">File</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2 text-right">Rows</th>
                    <th className="px-2 py-2 text-right">Committed</th>
                    <th className="px-2 py-2 text-right">Failed</th>
                    <th className="px-2 py-2">By</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {(list.data?.data ?? []).map((job) => (
                    <JobRow
                      key={job.id}
                      job={job}
                      onOpen={() => navigate(`/${config.routePrefix}/imports/jobs/${job.id}`)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <UzPagination
              page={page}
              pageSize={25}
              totalItems={list.data?.pagination.totalItems ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </UzCard>
    </PageShell>
  );
};

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

const JobRow = ({
  job,
  onOpen,
}: {
  job: ImportJobListItem;
  onOpen: () => void;
}): React.JSX.Element => {
  const tone: Record<
    ImportJobListItem['status'],
    'success' | 'destructive' | 'warning' | 'secondary' | 'default'
  > = {
    VALIDATING: 'secondary',
    READY: 'success',
    PARTIAL: 'warning',
    COMMITTING: 'secondary',
    COMPLETED: 'success',
    FAILED: 'destructive',
  };
  const started = new Date(job.startedAt);
  return (
    <tr
      className="cursor-pointer border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50"
      onClick={onOpen}
    >
      <td className="px-2 py-3 text-zinc-700">
        {started.toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </td>
      <td className="px-2 py-3 text-zinc-700">
        <span className="block max-w-[260px] truncate" title={job.fileName}>
          {job.fileName}
        </span>
        <span className="text-[11px] text-zinc-400">
          {(job.fileSizeBytes / 1024).toFixed(1)} KB
        </span>
      </td>
      <td className="px-2 py-3">
        <UzBadge tone={tone[job.status]}>{job.status}</UzBadge>
      </td>
      <td className="px-2 py-3 text-right text-zinc-700">{job.totalRows}</td>
      <td className="px-2 py-3 text-right font-medium text-emerald-700">{job.committedRows}</td>
      <td className="px-2 py-3 text-right font-medium text-red-700">{job.failedRows}</td>
      <td className="px-2 py-3 text-zinc-700">{job.createdByName ?? '-'}</td>
      <td className="px-2 py-3 text-zinc-400">
        <ChevronRight className="h-4 w-4" />
      </td>
    </tr>
  );
};
